import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import downloadFile from "_shared/_utilities/download-file";
import AHBoolean from "_shared/booleans/boolean";
import Guid from "_shared/guids/guid";
import DocumentAPIService from "documents/_api/document-api-service";
import DocumentSelector from "documents/_views/selectors/document-selector";
import DocumentUploadOverlay from "documents/_views/uploader/document-upload-overlay";
import Document from "documents/document";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useSession } from "users/__session/session-context";
import { Masked } from "work/_shared/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import { IssueContainer } from "work/proposals/issues/_views/proposal-issue-container";
import ProposalIssues, { ProposalIssueLevel } from "work/proposals/issues/proposal-issues";
import Proposal from "work/proposals/proposal";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import SelectedDocumentList from "work/work-agreements/contracts/_views/selected-document-list";
import { WorkDocumentType } from "work/work-agreements/contracts/types";
import WorkDocument from "work/work-agreements/contracts/work-document";

const ListContainer = styled(Grid, {
  shouldForwardProp: (prop) => prop !== "waived",
})<{ waived: boolean }>(({ waived }) => ({
  flexWrap: "nowrap",
  minHeight: "20rem",
  opacity: waived ? 0.25 : 1,
  pointerEvents: waived ? "none" : "auto",
  width: "100%",
}));
const DocumentSelectorContainer = styled(Grid)(() => ({
  display: "flex",
}));
const SelectedDocs = styled(SelectedDocumentList)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginTop: theme.spacing(2),
  },
  maxHeight: "60vh",
  marginLeft: theme.spacing(2),
  overflowY: "auto",
}));
const HeadControls = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  paddingLeft: theme.spacing(2),
}));
const Uploader = styled(DocumentUploadOverlay)(() => ({
  backgroundColor: "rgba(250, 250, 250, 0.5)",
  backdropFilter: "blur(5px) saturate(200%)",
  position: "absolute",
  top: 0,
  left: 0,
  margin: "1rem",
  width: "calc(100% - 2rem)",
  height: "calc(100% - 2rem)",
  zIndex: 9999,
}));

type ConflictsUploadProps = {
  activeReviewField?: ProposalField;
  proposalBuilder: ProposalBuilder;
  issues?: ProposalIssues;
  proposal?: Proposal;
  commentThreads?: CommentThread[];
  disableEditing?: boolean;
  isSaved: boolean;
  commentsToolTip: string;
  onCommentsClicked?: (field: ProposalField, name?: string) => void;
  onProposalBuilderUpdated: (updatedProposalBuilder: ProposalBuilder) => void;
};

export default function ConflictsTab(props: Readonly<ConflictsUploadProps>) {
  const {
    activeReviewField,
    proposalBuilder,
    commentThreads,
    disableEditing,
    commentsToolTip,
    onCommentsClicked,
    onProposalBuilderUpdated,
    isSaved
  } = props;

  const [isDownloadingFile, setIsDownloadingFile] = React.useState<Guid | null>(null);
  const [creatingDocumentFromTemplate, setCreatingDocumentFromTemplate] = React.useState<Guid | null>(null);

  const issues = React.useMemo(
    () =>
      props.issues?.entries.filter((issue) =>
        [
          ProposalField.Conflicts,
          ProposalField.ConflictsDocument(issue.field.id ?? Guid.EMPTY),
          ProposalField.WaiveConflictsCheck,
        ].includes(issue.field)
      ),
    [props.issues?.entries]
  );

  const confirm = useConfirmDialog();
  const session = useSession();

  async function handleConflictsDocumentRemoved(documentId: Guid): Promise<void> {
    const response = await confirm({
      title: "Remove Conflicts Document",
      message: "Are you sure you want to remove this conflicts document?",
    });

    if (
      response === ConfirmResponse.Cancel ||
      Masked.isMasked(proposalBuilder.currentSpec.workAgreement.conflicts)
    )
      return;

    onProposalBuilderUpdated(
      proposalBuilder.setConflicts(
        (proposalBuilder.currentSpec.workAgreement.conflicts ?? []).filter(
          (doc) => !doc.id.isEqualTo(documentId)
        )
      )
    );
    onCommentsClicked?.(ProposalField.Conflicts, undefined);
  }

  async function handleDownloadDocumentById(
    documentId: Guid,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): Promise<void> {
    event?.stopPropagation();
    try {
      setIsDownloadingFile(documentId);
      const documentApiService = new DocumentAPIService(session);
      const conflictsDocument = await documentApiService.downloadDocument(documentId);
      await downloadFile(conflictsDocument);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to download conflicts document. Please try again", { variant: "error" });
    } finally {
      setIsDownloadingFile(null);
    }
  }

  function handleDocumentSelectionChanged(selectedDocs: Document[], deselectedDocs: Document[]) {
    if (Masked.isMasked(proposalBuilder.currentSpec?.workAgreement.conflicts))
      throw new Error("You do not have permission to view contracts.");
    let currentDocuments = [...(proposalBuilder.currentSpec?.workAgreement.conflicts ?? [])];

    for (const selectedDoc of selectedDocs) {
      if (!currentDocuments.some((doc) => doc.id.isEqualTo(selectedDoc.id))) {
        const workDocument = WorkDocument.fromDocument(selectedDoc, WorkDocumentType.Conflicts);
        currentDocuments.push(workDocument);
      }
    }

    for (const deselectedDoc of deselectedDocs) {
      currentDocuments = currentDocuments.filter((doc) => !doc.id.isEqualTo(deselectedDoc.id));
    }

    onProposalBuilderUpdated(proposalBuilder.setConflicts(currentDocuments));
    onCommentsClicked?.(ProposalField.Conflicts, undefined);
  }

  function handleConflictsDocumentAdded(document: Document) {
    if (Masked.isMasked(proposalBuilder.currentSpec?.workAgreement.conflicts))
      throw new Error("You do not have permission to view contracts.");
    const updatedDocuments = proposalBuilder.currentSpec.workAgreement.conflicts?.concat(
      WorkDocument.fromDocument(document, WorkDocumentType.Conflicts)
    );

    setCreatingDocumentFromTemplate(null);
    onProposalBuilderUpdated(proposalBuilder.setConflicts(updatedDocuments ?? []));
    onCommentsClicked?.(ProposalField.Conflicts, undefined);
  }

  async function handleBeginCreateDocumentFromTemplate(templateId: Guid | undefined | null) {
    if (!templateId) return;
    setCreatingDocumentFromTemplate(templateId);
    await handleDownloadDocumentById(templateId);
  }

  function renderDocumentSelector() {
    if (Masked.isMasked(proposalBuilder.currentSpec?.workAgreement.conflicts)) {
      throw new Error("You do not have permission to view contracts.");
    }
    let selectedTemplateIds: Array<Guid> = [];
    for (const doc of proposalBuilder.currentSpec.workAgreement.conflicts ?? []) {
      selectedTemplateIds = [...selectedTemplateIds, ...doc.templateIds];
    }

    return (
      <DocumentSelector
        documentType="conflicts"
        selectedDocumentIds={proposalBuilder.currentSpec.workAgreement.conflicts?.map((doc) => doc.id) ?? []}
        selectedTemplateIds={selectedTemplateIds}
        onDocumentSelectionChanged={handleDocumentSelectionChanged}
        onCreateDocumentFromTemplate={handleBeginCreateDocumentFromTemplate}
      />
    );
  }

  async function handleConflictsCheckWaiverChanged(checked: AHBoolean) {
    if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement?.conflicts)) {
      throw new Error("You do not have permission to view contracts.");
    }
    if (proposalBuilder.currentSpec.workAgreement?.conflicts?.length) {
      const response = await confirm({
        title: "Remove Conflicts Documents?",
        message: "Waiving the conflicts check will remove the documents that are already on the proposal",
      });

      if (response === ConfirmResponse.Cancel) return;
    }

    onProposalBuilderUpdated(proposalBuilder.setConflictsCheckWaived(checked).setConflicts([]));
    onCommentsClicked?.(ProposalField.Conflicts, undefined);
  }

  if (
    Masked.isMasked(proposalBuilder.currentSpec.workAgreement.conflicts) ||
    Masked.isMasked(proposalBuilder.currentSpec.workAgreement.conflictsCheckWaived)
  ) {
    return "You do not have permission to view contracts.";
  }

  return (
    <>
      <HeadControls>
        {(issues?.length ?? 0) > 0 && (
          <IssueContainer active={issues?.some((issue) => issue.field.isEqualTo(activeReviewField)) ?? false}>
            {issues?.map((issue) => (
              <Typography
                key={issue.field?.cacheKey}
                color={issue.level === ProposalIssueLevel.Critical ? "error" : "warning"}
                variant="body1"
              >
                {issue.description}
              </Typography>
            ))}
          </IssueContainer>
        )}
        <FormControlLabel
          control={
            <Switch
              disabled={disableEditing}
              checked={proposalBuilder.currentSpec?.workAgreement.conflictsCheckWaived?.value ?? false}
              onChange={(_event, checked) => handleConflictsCheckWaiverChanged(new AHBoolean(checked))}
              color="primary"
            />
          }
          label={
            proposalBuilder.currentSpec?.workAgreement.conflictsCheckWaived?.value ? (
              <strong>Conflicts Check Waived</strong>
            ) : (
              "Conflicts Check Not Waived"
            )
          }
        />
        <CommentsButton
          field={ProposalField.Conflicts}
          isActive={activeReviewField?.isEqualTo(ProposalField.Conflicts)}
          toolTip={commentsToolTip}
          disabled={!isSaved}
          commentThreads={commentThreads}
          onCommentsClicked={
            onCommentsClicked
              ? () => {
                onCommentsClicked?.(ProposalField.Conflicts);
              }
              : undefined
          }
        />
      </HeadControls>
      <ListContainer
        waived={proposalBuilder.currentSpec?.workAgreement.conflictsCheckWaived?.value ?? false}
        container
        direction="row"
      >
        {!disableEditing && (
          <>
            <DocumentSelectorContainer size="grow">
              {renderDocumentSelector()}
            </DocumentSelectorContainer>
            <Grid>
              <Divider orientation="vertical" />
            </Grid>
          </>
        )}
        <Grid size={4}>
          <SelectedDocs
            documents={proposalBuilder.currentSpec?.workAgreement.conflicts ?? []}
            documentType={WorkDocumentType.Conflicts}
            downloadingFileId={isDownloadingFile}
            disableEditing={disableEditing}
            disableComments={!isSaved}
            commentThreads={commentThreads}
            commentsToolTip={commentsToolTip}
            onCommentsClicked={onCommentsClicked}
            onDownload={handleDownloadDocumentById}
            onDocumentRemoved={handleConflictsDocumentRemoved}
            onDocumentAdded={handleConflictsDocumentAdded}
          />
        </Grid>
      </ListContainer>
      {!disableEditing && creatingDocumentFromTemplate && (
        <Uploader
          session={session}
          documentType={WorkDocumentType.Conflicts}
          templateId={creatingDocumentFromTemplate}
          onDocumentUploaded={handleConflictsDocumentAdded}
          onClose={() => setCreatingDocumentFromTemplate(null)}
        />
      )}
    </>
  );
}
